import React from "react";
import { NavLink } from "react-router-dom";
import MomentsCreditsBanner from "./MomentsCreditsBanner";
import { useSecondModal } from "@helpers/hooks/useSecondModal";
import "./SuccessScene.css";

const FRAME_LABELS = { classic: "קלאסי", bold: "נועז", ever: "נקי", clean: "אותנטי", village: "כפרי", nordic: "נורדי" };
// Same opening coords the Personal Area uses, with WebP frame mockups.
const FRAME_MAP = {
  classic: { bg: "/assets/file/images/frame_classic.webp", left: "16.5%", top: "16.2%", width: "65.4%", height: "66.5%" },
  bold:    { bg: "/assets/file/images/frame_bold.webp",    left: "3.8%",  top: "3.8%",  width: "90.4%", height: "91%" },
  ever:    { bg: "/assets/file/images/frame_even.webp",    left: "3.5%",  top: "3.5%",  width: "90.8%", height: "91%" },
  clean:   { bg: "/assets/file/images/frame_clean.webp",   left: "13.8%", top: "14.2%", width: "70.4%", height: "70%" },
  nordic:  { bg: "/assets/file/images/frame_nordic.webp",  left: "3.8%",  top: "3.8%",  width: "90.4%", height: "91%" },
  village: { bg: "/assets/file/images/frame_village.webp", left: "16.5%", top: "16.2%", width: "65.4%", height: "66.5%" },
};
const TABLE = "/assets/images/success/table-top.webp";
const DUKI = "/mascot/poses/00-celebrate-master.webp";

const CalIcon = () => (<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
const PinIcon = () => (<svg viewBox="0 0 24 24"><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>);

const Steps = () => (
  <div className="ss-steps">
    <div className="ss-step on"><span className="ss-seg" /><span className="ss-lbl">התקבלה</span></div>
    <div className="ss-step"><span className="ss-seg" /><span className="ss-lbl">בהפקה</span></div>
    <div className="ss-step"><span className="ss-seg" /><span className="ss-lbl">במשלוח</span></div>
    <div className="ss-step"><span className="ss-seg" /><span className="ss-lbl">נמסרה</span></div>
  </div>
);

const Prints = ({ pile, ready, solo }) => (
  <>
    {pile.map((src, i) => (
      <div key={i} className={`ss-print p${i + 1}`}><img src={src} alt="" loading="lazy" /></div>
    ))}
    {ready !== undefined && (
      <div className={`ss-print ss-ready${solo ? " ss-solo" : ""}`}><img src={ready} alt="" loading="lazy" /></div>
    )}
  </>
);

const Duki = ({ className }) => (
  <div className={className}>
    <span className="ss-legshadow" />
    <img src={DUKI} alt="דוכי" />
  </div>
);

function Details({ data, centered, isMobile }) {
  const modal = useSecondModal();
  const { orderId, frameType, images, fullName, address, city, phone, deliveryDate, imageCount, creditsEarned, freeShipping } = data;
  const frame = FRAME_MAP[frameType] || FRAME_MAP.nordic;
  const label = FRAME_LABELS[frameType] || frameType || "מסגרת";
  return (
    <>
      <div style={centered ? { textAlign: "center" } : null}>
        <div className="ss-eyebrow">התשלום אושר</div>
        <h1 className="ss-h1">ההזמנה נקלטה בהצלחה</h1>
      </div>

      <div style={{ marginTop: 22 }}><Steps /></div>

      <div className="ss-row" style={{ marginTop: 10 }}>
        <div className="ss-ic"><CalIcon /></div>
        <div><div className="ss-k">צפי הגעה</div><div className="ss-v">{deliveryDate}</div></div>
        {freeShipping && <div className="ss-end">משלוח חינם</div>}
      </div>

      <div className="ss-product">
        <div className="ss-thumbs">
          {(images || []).slice(0, 4).map((src, i) => (
            <div key={i} className="ss-thumb" style={{ backgroundImage: `url(${frame.bg})` }}>
              <img src={src} alt="" loading="lazy" style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }} />
            </div>
          ))}
        </div>
        <div><div className="ss-pname">מסגרת {label}</div><div className="ss-pmeta">גודל 21×21 ס״מ</div></div>
        <div className="ss-count">×{imageCount}</div>
      </div>

      {(fullName || address) && (
        <div className="ss-row">
          <div className="ss-ic"><PinIcon /></div>
          <div>
            <div className="ss-k">נשלח אל</div>
            {fullName && <div className="ss-v">{fullName}</div>}
            <div className="ss-sm">{[address, city].filter(Boolean).join(", ")}{phone ? ` · ${phone}` : ""}</div>
          </div>
          {orderId && <div className="ss-end">#{orderId}</div>}
        </div>
      )}

      {creditsEarned > 0 && <MomentsCreditsBanner amount={creditsEarned} autoPlayDelay={400} />}

      <div className="ss-actions">
        <button type="button" className="ss-btn" onClick={() => modal("open", isMobile ? "personalAreaMobile" : "personalArea")}>מעקב אחר ההזמנה</button>
        <NavLink className="ss-btn ghost" to="/upload-your-image">התחל הזמנה חדשה</NavLink>
      </div>
    </>
  );
}

export default function SuccessScene({ isMobile, ...data }) {
  const list = (Array.isArray(data.images) ? data.images.filter(Boolean) : []);
  // No duplicates: the LAST distinct image is the single "ready to be framed" print
  // (plain, not framed); the rest (capped at 4) form the pile. 1 image -> one centered print.
  const ready = list.length ? list[list.length - 1] : "";
  const pile = list.slice(0, Math.max(0, list.length - 1)).slice(0, 4);
  const solo = pile.length === 0;

  if (isMobile) {
    return (
      <div className="ss-wrap">
        <div className="ss-mobile">
          <div className="ss-scene">
            <img className="ss-table" src={TABLE} alt="" />
            <div className="ss-pile"><Prints pile={pile} ready={undefined} /></div>
            <div className={`ss-print ss-ready${solo ? " ss-solo" : ""}`}><img src={ready} alt="" loading="lazy" /></div>
            <div className="ss-vig" />
          </div>
          <Duki className="ss-mascot" />
          <div className="ss-details"><Details data={data} centered isMobile /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-wrap">
      <div className="ss-desktop">
        <section className="ss-scenary">
          <img className="ss-table" src={TABLE} alt="" />
          <div className="ss-cluster"><Prints pile={pile} ready={ready} solo={solo} /></div>
          <div className="ss-vig" />
          <div className="ss-fade" />
        </section>
        <section className="ss-panel"><div className="ss-content"><Details data={data} isMobile={false} /></div></section>
        <Duki className="ss-duki" />
      </div>
    </div>
  );
}
